import { useLayoutEffect, useRef } from "react";

const FLIP_TRANSITION = "transform 0.22s cubic-bezier(0.2, 0, 0, 1)";
const MAX_FLIP_DELTA_PX = 120;

/** Label row inside a sortable item — used for hit-testing and compact slot height. */
export function getSortLabelElement(sortItemEl) {
  if (!sortItemEl) return null;
  return sortItemEl.querySelector("[data-sort-label]") || sortItemEl;
}

function idsEqual(a, b) {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/**
 * When the pointer crosses 50% of a sibling's height, insert the dragged id before it.
 * Dragged item must be out of flow (placeholder in list + fixed ghost).
 *
 * @param {number} pointerY
 * @param {string} scopeKey
 * @param {string} draggedId
 * @param {string[]} previewIds
 * @returns {string[]}
 */
export function computePreviewIdsFromPointer(pointerY, scopeKey, draggedId, previewIds) {
  if (!previewIds?.length) return previewIds;

  const container = document.querySelector(`[data-playEngine-sort-list="${scopeKey}"]`);
  if (!container) return previewIds;

  const others = previewIds.filter((id) => id !== draggedId);
  let insertAt = others.length;

  for (let i = 0; i < others.length; i++) {
    const el = container.querySelector(`[data-sort-id="${others[i]}"]`);
    if (!el) continue;
    const labelEl = getSortLabelElement(el);
    const rect = labelEl.getBoundingClientRect();
    if (rect.height <= 0) continue;
    const midpoint = rect.top + rect.height / 2;
    if (pointerY < midpoint) {
      insertAt = i;
      break;
    }
  }

  const next = [...others];
  next.splice(insertAt, 0, draggedId);
  return idsEqual(next, previewIds) ? previewIds : next;
}

/**
 * Animate sibling list reordering (FLIP) for all sort lists in the document.
 * @param {{ measureOnly?: boolean }} [options] measureOnly — update cached rects without animating
 */
export function flipPlayEngineSortLists(options = {}) {
  const { measureOnly = false } = options;
  document.querySelectorAll("[data-playEngine-sort-list]").forEach((container) => {
    if (!container._flipPrevRects) container._flipPrevRects = new Map();
    const prevRects = container._flipPrevRects;

    container.querySelectorAll("[data-sort-id]").forEach((el) => {
      const id = el.dataset.sortId;
      const rect = el.getBoundingClientRect();
      const prev = prevRects.get(id);
      if (prev && !measureOnly) {
        const dy = prev.top - rect.top;
        if (Math.abs(dy) > 1 && Math.abs(dy) <= MAX_FLIP_DELTA_PX) {
          el.style.transform = `translateY(${dy}px)`;
          el.style.transition = "transform 0s";
          requestAnimationFrame(() => {
            el.style.transition = FLIP_TRANSITION;
            el.style.transform = "";
          });
        }
      }
      prevRects.set(id, rect);
    });
  });
}

/** Clear FLIP caches and inline transforms after a drag ends. */
export function resetPlayEngineSortListFlip() {
  document.querySelectorAll("[data-playEngine-sort-list]").forEach((container) => {
    container._flipPrevRects = new Map();
    container.querySelectorAll("[data-sort-id]").forEach((el) => {
      el.style.transform = "";
      el.style.transition = "";
    });
  });
}

/**
 * @param {string} sortKey Serialized order — changes trigger FLIP.
 * @param {React.MutableRefObject<boolean>} [skipFlipRef] when true, skip animation once (drag drop)
 * @param {React.MutableRefObject<boolean>} [isDraggingRef] while true, only measure — no FLIP animation
 */
export function usePlayEngineSortListFlip(sortKey, skipFlipRef, isDraggingRef) {
  const isFirstRef = useRef(true);

  useLayoutEffect(() => {
    if (skipFlipRef?.current) {
      skipFlipRef.current = false;
      resetPlayEngineSortListFlip();
      flipPlayEngineSortLists({ measureOnly: true });
      return;
    }
    if (isDraggingRef?.current) {
      resetPlayEngineSortListFlip();
      flipPlayEngineSortLists({ measureOnly: true });
      return;
    }
    if (isFirstRef.current) {
      isFirstRef.current = false;
      flipPlayEngineSortLists({ measureOnly: true });
      return;
    }
    flipPlayEngineSortLists();
  }, [sortKey, skipFlipRef, isDraggingRef]);
}
